import jsep from 'jsep';
import {AND, AVERAGE, COUNTIF, MAX, MIN, OR, SUM} from '@formulajs/formulajs';

/**
 * Impact-assessment formula engine.
 *
 * Formulas are authored in a formula.js (Excel-style) dialect and stored in the
 * DB, e.g. `MAX(values)==3`, `OR(MAX(values)==4, AND(COUNTIF(values,5)>=2, ...))`.
 * `values` is bound at evaluation time to the numeric array of selected scores.
 * Note: this dialect uses `==` (not Excel `=`) because the operators are real
 * comparison operators, not a spreadsheet assignment.
 *
 * Security: formulas are user-authored config that is later evaluated in *other*
 * users' browsers, so the evaluator must never be able to reach JS internals.
 * We parse with `jsep` (which only builds an AST — it never executes anything)
 * and walk that AST with a strict allowlist interpreter below. Only the node
 * types, operators, the single `values` identifier, and the whitelisted
 * functions are honored; anything else (member access, unknown identifiers,
 * arrays, ternaries, …) throws and the formula is rejected. This makes the
 * `constructor`-chain RCE class that affected general evaluators structurally
 * impossible — there is no node type that can reach a function constructor.
 */

/** The only functions a formula may call — delegated to @formulajs/formulajs. */
const FUNCTIONS: Record<string, (...args: never[]) => unknown> = {
  MAX,
  MIN,
  COUNTIF,
  AND,
  OR,
  SUM,
  AVERAGE,
};

/** The only identifier a formula may reference (bound to the scores array). */
const VALUES_IDENTIFIER = 'values';

type BinaryOp = (typeof BINARY_OPS)[number];
const BINARY_OPS = [
  '==', '!=', '===', '!==', '<', '>', '<=', '>=',
  '+', '-', '*', '/', '%', '&&', '||',
] as const;

const UNARY_OPS = ['-', '+', '!'] as const;

function applyBinary(op: string, l: unknown, r: unknown): unknown {
  const a = l as number;
  const b = r as number;
  switch (op) {
    case '==': return a == b; // spreadsheet-style loose comparison (intentional)
    case '!=': return a != b;
    case '===': return a === b;
    case '!==': return a !== b;
    case '<': return a < b;
    case '>': return a > b;
    case '<=': return a <= b;
    case '>=': return a >= b;
    case '+': return a + b;
    case '-': return a - b;
    case '*': return a * b;
    case '/': return a / b;
    case '%': return a % b;
    case '&&': return Boolean(l) && Boolean(r);
    case '||': return Boolean(l) || Boolean(r);
    default: throw new Error(`Unsupported operator: ${op}`);
  }
}

/**
 * Recursively evaluate a jsep AST node against the bound `values` array.
 * Throws on any node type / operator / identifier / function outside the
 * allowlist — callers treat a throw as "formula does not hold".
 */
function evalNode(node: jsep.Expression, values: number[]): unknown {
  switch (node.type) {
    case 'Literal':
      return (node as jsep.Literal).value;

    case 'Identifier': {
      const name = (node as jsep.Identifier).name;
      if (name === VALUES_IDENTIFIER) return values;
      throw new Error(`Unknown identifier: ${name}`);
    }

    case 'UnaryExpression': {
      const n = node as jsep.UnaryExpression;
      if (!(UNARY_OPS as readonly string[]).includes(n.operator)) {
        throw new Error(`Unsupported unary operator: ${n.operator}`);
      }
      const arg = evalNode(n.argument, values) as number;
      if (n.operator === '-') return -arg;
      if (n.operator === '+') return +arg;
      return !arg;
    }

    case 'BinaryExpression': {
      const n = node as jsep.BinaryExpression;
      if (!(BINARY_OPS as readonly string[]).includes(n.operator)) {
        throw new Error(`Unsupported operator: ${n.operator}`);
      }
      return applyBinary(
        n.operator as BinaryOp,
        evalNode(n.left, values),
        evalNode(n.right, values)
      );
    }

    case 'CallExpression': {
      const n = node as jsep.CallExpression;
      // Callee must be a bare identifier in the function allowlist —
      // no member calls (`x.constructor(...)`), no computed callees.
      if (n.callee.type !== 'Identifier') {
        throw new Error('Only direct function calls are allowed');
      }
      const fnName = (n.callee as jsep.Identifier).name;
      const fn = FUNCTIONS[fnName];
      if (!fn) throw new Error(`Unknown function: ${fnName}`);
      const args = n.arguments.map((a) => evalNode(a, values));
      return fn(...(args as never[]));
    }

    default:
      throw new Error(`Unsupported expression: ${node.type}`);
  }
}

const cache = new Map<string, jsep.Expression>();

function parse(formula: string): jsep.Expression {
  let ast = cache.get(formula);
  if (!ast) {
    ast = jsep(formula);
    cache.set(formula, ast);
  }
  return ast;
}

/**
 * Evaluate a predicate formula against a numeric array.
 * Returns `true` only when the formula strictly evaluates to boolean `true`;
 * malformed, unsafe, or empty formulas return `false` (never throw) so a bad
 * config can't crash the assessment panel.
 */
export function evalFormula(formula: string | null | undefined, values: number[]): boolean {
  if (!formula || !formula.trim()) return false;
  try {
    return evalNode(parse(formula), values) === true;
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn(`[formulaEngine] failed to evaluate "${formula}":`, err);
    }
    return false;
  }
}

/**
 * Static validation for save-time form checks: returns `true` if the formula
 * parses and contains only allowlisted nodes/operators/identifiers/functions.
 * An empty formula is considered valid (the field is optional). This walks the
 * AST with a dummy value array so it never depends on actual scores.
 */
export function isFormulaValid(formula: string | null | undefined): boolean {
  if (!formula || !formula.trim()) return true;
  try {
    evalNode(parse(formula), []);
    return true;
  } catch {
    return false;
  }
}
